import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, CheckCircle, Shield, AlertCircle, ExternalLink, Copy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [resetLink, setResetLink] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/admin/forgot-password`, { email });
      
      // Check if reset link should be shown (email not configured)
      if (response.data.show_reset_link && response.data.reset_url) {
        setResetLink({
          url: response.data.reset_url,
          expiresAt: response.data.expires_at,
          note: response.data.note
        });
      }
      
      setSubmitted(true);
      toast.success(response.data.message || 'Reset link sent if account exists');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to process request. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const copyResetLink = () => {
    if (resetLink?.url) {
      navigator.clipboard.writeText(resetLink.url);
      toast.success('Reset link copied to clipboard');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-2xl mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              {resetLink ? 'Reset Link Generated' : 'Check Your Email'}
            </h1>
            <p className="text-slate-400">
              {resetLink 
                ? 'Use the link below to reset your admin password.'
                : `If an admin account exists for ${email}, we've sent a password reset link.`}
            </p>
          </div>

          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
            {/* Show reset link directly if email not configured */}
            {resetLink ? (
              <div className="space-y-4">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <p className="text-amber-400 text-sm mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Email service not configured - use this link instead
                  </p>
                  <div className="bg-slate-900 rounded-lg p-3 break-all text-sm text-slate-300 font-mono">
                    {resetLink.url}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={copyResetLink}
                      variant="outline"
                      size="sm"
                      className="border-slate-600 text-slate-300 hover:text-white"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </Button>
                    <a href={resetLink.url} target="_blank" rel="noopener noreferrer">
                      <Button
                        size="sm"
                        className="bg-trust-blue hover:bg-trust-blue-hover text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open Link
                      </Button>
                    </a>
                  </div>
                </div>
                {resetLink.note && (
                  <p className="text-slate-500 text-sm">{resetLink.note}</p>
                )}
              </div>
            ) : (
              <div className="space-y-4 text-sm text-slate-400">
                <p>
                  <strong className="text-white">Didn't receive the email?</strong>
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Check your spam/junk folder</li>
                  <li>Verify you entered the correct admin email</li>
                  <li>Wait a few minutes and try again</li>
                </ul>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-slate-700">
              <Link to="/admin/login">
                <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Admin Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Admin Badge */}
        <div className="flex justify-center mb-6">
          <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 px-4 py-1.5 text-sm">
            <Shield className="w-4 h-4 mr-2" />
            Admin Password Reset
          </Badge>
        </div>

        <div className="text-center mb-8">
          <img 
            src="/staypairhomes-logo.png" 
            alt="Stay Pair Homes" 
            className="h-16 w-auto mx-auto mb-4 object-contain"
          />
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Forgot Password?
          </h1>
          <p className="text-slate-400">
            Enter your admin email address and we'll send you a reset link.
          </p>
        </div>

        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">Admin Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  placeholder="admin@staypairhomes.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="reset-email"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-trust-blue hover:bg-trust-blue-hover text-white h-12"
              disabled={loading}
              data-testid="send-reset-link"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Reset Link
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-700">
            <Link to="/admin/login">
              <Button variant="ghost" className="w-full text-slate-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin Login
              </Button>
            </Link>
          </div>
        </div>

        <p className="mt-6 text-center text-slate-500 text-sm">
          For security reasons, we don't reveal whether an account exists.
        </p>
      </div>
    </div>
  );
};

export default AdminForgotPasswordPage;
